import React, { useCallback, useMemo, useState } from 'react'
import uniqBy from 'lodash/uniqBy'
import { makeStyles, Theme } from 'transfix-ui/core'
import { BooleanParam } from 'use-query-params'
import { useQueryParams } from 'utils/hooks/useQueryParams'
import { CarrierSearchContainer } from 'apps/fleet-planner/components/carrier-search'
import { ISearchedMotorCarrier } from 'apps/fleet-planner/components/carrier-search/types'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useJoinMotorCarriers } from 'apps/fleet-planner/hooks/useJoinMotorCarriers'
import { IMotorCarrier } from 'apps/fleet-planner/types'
import { IMobileWebViewParams } from 'apps/fleet-planner/types/mobileApp'
import { ToastState, FormModal } from 'transfix-ui'

const useAddCarrierModalStyles = makeStyles<Theme, { mobileApp: boolean }>({
  header: {
    display: ({ mobileApp }) => (mobileApp ? 'none' : undefined),
  },
  mobileHeader: { display: 'none' },
})

export interface IAddCarrierModalProps {
  onSuccess: (toastState: Omit<ToastState, 'toastOpen'>) => void
  onClose: () => void
  initialCarriersDotNumbers: IMotorCarrier['dotNumber'][]
}

export const AddCarrierModal = ({
  onSuccess,
  onClose,
  initialCarriersDotNumbers = [],
}: IAddCarrierModalProps) => {
  const { trackEvent } = useFMSAnalytics()
  const { joinMotorCarriers } = useJoinMotorCarriers()

  const [addedCarriers, setAddedCarriers] = useState<ISearchedMotorCarrier[]>([])
  const [seenError, setSeenError] = useState<boolean>(false)

  // We can allow users to add existing carriers to the list but will exclude them in the join request
  const updatedCarriers: ISearchedMotorCarrier[] = useMemo(
    () =>
      addedCarriers.filter(
        ({ dotNumber }) => dotNumber && !initialCarriersDotNumbers.includes(dotNumber)
      ),
    [initialCarriersDotNumbers, addedCarriers]
  )

  const trackError = useCallback(() => {
    if (!seenError) {
      trackEvent('Carrier Management', 'CARRIERS_SUBMIT_BUTTON_CLICKED')
      setSeenError(true)
    }
  }, [seenError])

  const addCarrierHandler = (addedCarrier: ISearchedMotorCarrier) => {
    if (addedCarrier.maskedAdminEmails.length) {
      trackError()
    }
    setAddedCarriers(amc => uniqBy([...amc, addedCarrier], 'dotNumber'))
  }

  const removeCarrierHandler = (removedCarrierIdx: number) => {
    const removedCarrier = addedCarriers[removedCarrierIdx]
    setAddedCarriers(amc => amc.filter(mc => mc.dotNumber !== removedCarrier.dotNumber))
  }

  const submitHandler = () => {
    trackEvent('Carrier Management', 'CARRIERS_SUBMIT_BUTTON_CLICKED')
    if (updatedCarriers) {
      joinMotorCarriers(updatedCarriers).then(() => {
        const toastMessage = 'Carriers Added.'
        onSuccess({
          toastType: 'default',
          toastMessage,
        })
      })
    }
  }

  const [{ mobileApp }] = useQueryParams<IMobileWebViewParams>({
    mobileApp: BooleanParam,
  })

  const classes = useAddCarrierModalStyles({ mobileApp: !!mobileApp })

  return (
    <FormModal
      title='Add Carriers'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
        },
        {
          label: `Add ${addedCarriers.length === 1 ? 'Carrier' : 'Carriers'}`,
          type: 'confirm',
          disabled: !addedCarriers.length,
          action: submitHandler,
        },
      ]}
      open
      onClose={onClose}
      size={6}
      headerClassName={classes.header}>
      <CarrierSearchContainer
        onAddCarrier={addCarrierHandler}
        onRemoveCarrier={removeCarrierHandler}
        carriers={addedCarriers}
        onError={trackError}
      />
    </FormModal>
  )
}
